import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, JsonPipe, SlicePipe } from '@angular/common';
import { BaseRules, TextValues } from './iowa-model';
import { UimuiBaseStateComponent } from '../uimui-base-state.component';
import { TruncateModule } from 'axis-components/truncate';

@Component({
  selector: 'app-iowa',
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    TruncateModule,
    TooltipModule,
  ],
  templateUrl: './iowa.component.html',
  styleUrl: './iowa.component.scss',
})
export class IowaComponent extends UimuiBaseStateComponent<
  BaseRules,
  TextValues
> {
  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('iowa', form);
    }
  }
  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('iowa', null);
    }
  }
  bothRadioButtonsChecked: boolean = false;
  private isUpdatingSync: boolean = false;

  private shouldSyncSplitLimits(): boolean {
    const levelOneUMValue = this.reactiveForm?.get('levelOneUM_Radio')?.value;
    const levelOneUIMValue = this.reactiveForm?.get('levelOneUIM_Radio')?.value;

    const selectedUMValue = levelOneUMValue?.value || levelOneUMValue;
    const selectedUIMValue = levelOneUIMValue?.value || levelOneUIMValue;

    const isUMRejectSelected = selectedUMValue === 'Reject_LevelOneUM_Radio';
    const isUIMRejectSelected = selectedUIMValue === 'Reject_LevelOneUIM_Radio';
    const isUMLimitsSelected = selectedUMValue === 'Limits_LevelOneUM_Radio';
    const isUIMLimitsSelected = selectedUIMValue === 'Limits_LevelOneUIM_Radio';

    // Do not sync when one side is Reject and the other is Limits.
    if (
      (isUMRejectSelected && isUIMLimitsSelected) ||
      (isUMLimitsSelected && isUIMRejectSelected)
    ) {
      return false;
    }

    // Keep existing behavior: allow sync when both UM and UIM are Limits.
    return isUMLimitsSelected && isUIMLimitsSelected;
  }

  checkBothRadioButtons(): void {
    if (this.reactiveForm) {
      const umRadio = this.reactiveForm.get('levelOneUM_Radio')?.value;
      const uimRadio = this.reactiveForm.get('levelOneUIM_Radio')?.value;

      // Hide text if either reject button is selected
      if (
        umRadio?.value === 'Reject_LevelOneUM_Radio' ||
        uimRadio?.value === 'Reject_LevelOneUIM_Radio'
      ) {
        this.bothRadioButtonsChecked = false;
      } else {
        // Show text only if both limits buttons are selected
        this.bothRadioButtonsChecked =
          umRadio?.value === 'Limits_LevelOneUM_Radio' &&
          uimRadio?.value === 'Limits_LevelOneUIM_Radio';
      }
    }
  }

  changeOnSelection(data: any, formControlName: string) {
    if (
      !data ||
      !this.reactiveForm ||
      !formControlName ||
      this.isUpdatingSync
    ) {
      return;
    }

    // Handle synchronization between UM and UIM for both radio buttons and dropdowns
    // This ensures that when a user selects coverage options for UM, the corresponding
    // UIM coverage is automatically set to match, and vice versa
    this.isUpdatingSync = true;
    switch (formControlName) {
      case 'UM_SplitLimits_DropDown':
        if (!this.shouldSyncSplitLimits()) {
          break;
        }
        // When UM Split Limits is selected, sync with UIM Split Limits if visible
        const uimSplitControl = this.reactiveForm.get(
          'UIM_SplitLimits_DropDown',
        );
        if (uimSplitControl && this.baseRules?.['UIM_SplitLimits_DropDown']) {
          uimSplitControl.setValue(data, { emitEvent: false });
          uimSplitControl.updateValueAndValidity();
        }
        break;

      case 'UM_CombinedSingleLimit_DropDown':
        if (!this.shouldSyncSplitLimits()) {
          break;
        }
        // When UM Combined Single Limit is selected, sync with UIM Combined Single Limit if visible
        const uimCombinedControl = this.reactiveForm.get(
          'UIM_CombinedSingleLimit_DropDown',
        );
        if (
          uimCombinedControl &&
          this.baseRules?.['UIM_CombinedSingleLimit_DropDown']
        ) {
          uimCombinedControl.setValue(data, { emitEvent: false });
          uimCombinedControl.updateValueAndValidity();
        }
        break;

      case 'UIM_SplitLimits_DropDown':
        if (!this.shouldSyncSplitLimits()) {
          break;
        }
        // When UIM Split Limits is selected, sync with UM Split Limits if visible
        const umSplitControl = this.reactiveForm.get('UM_SplitLimits_DropDown');
        if (umSplitControl && this.baseRules?.['UM_SplitLimits_DropDown']) {
          umSplitControl.setValue(data, { emitEvent: false });
          umSplitControl.updateValueAndValidity();
        }
        break;

      case 'UIM_CombinedSingleLimit_DropDown':
        if (!this.shouldSyncSplitLimits()) {
          break;
        }
        // When UIM Combined Single Limit is selected, sync with UM Combined Single Limit if visible
        const umCombinedControl = this.reactiveForm.get(
          'UM_CombinedSingleLimit_DropDown',
        );
        if (
          umCombinedControl &&
          this.baseRules?.['UM_CombinedSingleLimit_DropDown']
        ) {
          umCombinedControl.setValue(data, { emitEvent: false });
          umCombinedControl.updateValueAndValidity();
        }
        break;
    }
    this.isUpdatingSync = false;
  }

  // Override eventHook to handle radio button synchronization between UM and UIM
  override eventHook(value: any): void {
    if (!value || !this.reactiveForm || this.isUpdatingSync) {
      return;
    }

    // Check if both Limits radio buttons are selected or if reject buttons are selected
    if (
      value === 'Limits_LevelOneUM_Radio' ||
      value === 'Limits_LevelOneUIM_Radio' ||
      value === 'Reject_LevelOneUM_Radio' ||
      value === 'Reject_LevelOneUIM_Radio'
    ) {
      this.checkBothRadioButtons();
    }

    this.isUpdatingSync = true;

    // Handle UM radio selections - sync with UIM radio buttons
    switch (value) {
      case 'UMSplitLimits_LevelTwoUM_Radio':
        // When UM Split Limits radio is selected, also select UIM Split Limits radio if visible
        const uimSplitRadio = this.reactiveForm.get('levelTwoUIM_Radio');
        if (
          uimSplitRadio &&
          this.baseRules?.['UIMSplitLimits_LevelTwoUIM_Radio']
        ) {
          uimSplitRadio.setValue('UIMSplitLimits_LevelTwoUIM_Radio', {
            emitEvent: false,
          });
          uimSplitRadio.updateValueAndValidity();
          // Trigger dynamic rules for UIM split selection
          this.setDynamicRules('UIMSplitLimits_LevelTwoUIM_Radio');
        }
        break;

      case 'UMCombinedSingleLimit_LevelTwoUM_Radio':
        // When UM Combined Single Limit radio is selected, also select UIM Combined Single Limit radio if visible
        const uimCombinedRadio = this.reactiveForm.get('levelTwoUIM_Radio');
        if (
          uimCombinedRadio &&
          this.baseRules?.['UIMCombinedSingleLimit_LevelTwoUIM_Radio']
        ) {
          uimCombinedRadio.setValue(
            'UIMCombinedSingleLimit_LevelTwoUIM_Radio',
            { emitEvent: false },
          );
          uimCombinedRadio.updateValueAndValidity();
          // Trigger dynamic rules for UIM combined selection
          this.setDynamicRules('UIMCombinedSingleLimit_LevelTwoUIM_Radio');
        }
        break;

      case 'UIMSplitLimits_LevelTwoUIM_Radio':
        // When UIM Split Limits radio is selected, also select UM Split Limits radio if visible
        const umSplitRadio = this.reactiveForm.get('levelTwoUM_Radio');
        if (
          umSplitRadio &&
          this.baseRules?.['UMSplitLimits_LevelTwoUM_Radio']
        ) {
          umSplitRadio.setValue('UMSplitLimits_LevelTwoUM_Radio', {
            emitEvent: false,
          });
          umSplitRadio.updateValueAndValidity();
          // Trigger dynamic rules for UM split selection
          this.setDynamicRules('UMSplitLimits_LevelTwoUM_Radio');
        }
        break;

      case 'UIMCombinedSingleLimit_LevelTwoUIM_Radio':
        // When UIM Combined Single Limit radio is selected, also select UM Combined Single Limit radio if visible
        const umCombinedRadio = this.reactiveForm.get('levelTwoUM_Radio');
        if (
          umCombinedRadio &&
          this.baseRules?.['UMCombinedSingleLimit_LevelTwoUM_Radio']
        ) {
          umCombinedRadio.setValue('UMCombinedSingleLimit_LevelTwoUM_Radio', {
            emitEvent: false,
          });
          umCombinedRadio.updateValueAndValidity();
          // Trigger dynamic rules for UM combined selection
          this.setDynamicRules('UMCombinedSingleLimit_LevelTwoUM_Radio');
        }
        break;

      case 'Reject_LevelOneUM_Radio': {
        // When UM Reject Level One is selected, hide UIM Level Two radio options
        this.baseRules = {
          ...this.baseRules,
          UMSplitLimits_LevelTwoUM_Radio: 'hide',
          UMCombinedSingleLimit_LevelTwoUM_Radio: 'hide',
        };

        // Reset the UIM Level Two radio and related dropdowns
        const umLevelTwoRadio = this.reactiveForm.get('levelTwoUM_Radio');
        const UimLevelOneRadio = this.reactiveForm.get(
          'UM_CombinedSingleLimit_DropDown',
        );
        const UmLevelOneRadio = this.reactiveForm.get(
          'UM_SplitLimits_DropDown',
        );

        if (umLevelTwoRadio) {
          umLevelTwoRadio.setValue('levelTwoUM_Radio', { emitEvent: false });
          umLevelTwoRadio.updateValueAndValidity();
          this.setDynamicRules('levelTwoUM_Radio');
        }
        if (UimLevelOneRadio) {
          UimLevelOneRadio.setValue('UM_CombinedSingleLimit_DropDown', {
            emitEvent: false,
          });
          UimLevelOneRadio.updateValueAndValidity();
          this.setDynamicRules('UM_CombinedSingleLimit_DropDown');
        }
        if (UmLevelOneRadio) {
          UmLevelOneRadio.setValue('UM_SplitLimits_DropDown', {
            emitEvent: false,
          });
          UmLevelOneRadio.updateValueAndValidity();
          this.setDynamicRules('UM_SplitLimits_DropDown');
        }

        // Trigger dynamic rules for reject selection
        this.setDynamicRules('Reject_LevelOneUM_Radio');
        break;
      }
    }
    this.isUpdatingSync = false;
  }

  // Override setDynamicRules to preserve UM/UIM dropdown values during sync operations only
  override setDynamicRules(control: string): void {
    if (this.isUpdatingSync) {
      // During sync operations, selectively preserve only compatible dropdown values
      const preservedValues = {
        umSplit: this.reactiveForm?.get('UM_SplitLimits_DropDown')?.value,
        uimSplit: this.reactiveForm?.get('UIM_SplitLimits_DropDown')?.value,
        umCombined: this.reactiveForm?.get('UM_CombinedSingleLimit_DropDown')
          ?.value,
        uimCombined: this.reactiveForm?.get('UIM_CombinedSingleLimit_DropDown')
          ?.value,
      };

      super.setDynamicRules(control);

      // Only restore values that are compatible with the control being set
      const isSplitControl = control.includes('SplitLimits');
      const isCombinedControl = control.includes('CombinedSingleLimit');

      if (isSplitControl) {
        // When setting split controls, only restore split dropdowns
        if (preservedValues.umSplit) {
          this.reactiveForm
            ?.get('UM_SplitLimits_DropDown')
            ?.setValue(preservedValues.umSplit, { emitEvent: false });
        }
        if (preservedValues.uimSplit) {
          this.reactiveForm
            ?.get('UIM_SplitLimits_DropDown')
            ?.setValue(preservedValues.uimSplit, { emitEvent: false });
        }
      } else if (isCombinedControl) {
        // When setting combined controls, only restore combined dropdowns
        if (preservedValues.umCombined) {
          this.reactiveForm
            ?.get('UM_CombinedSingleLimit_DropDown')
            ?.setValue(preservedValues.umCombined, { emitEvent: false });
        }
        if (preservedValues.uimCombined) {
          this.reactiveForm
            ?.get('UIM_CombinedSingleLimit_DropDown')
            ?.setValue(preservedValues.uimCombined, { emitEvent: false });
        }
      }
    } else {
      // For user interactions, let dynamic rules handle clearing as configured
      super.setDynamicRules(control);
    }
  }
}
